// E8BasketApplesClosedTask.js
// Zadanie zamkniete E8 (2026 zad. 7) – zadanie tekstowe (uklad warunkow):
// B koszy, lacznie T owocow, w kazdym koszu tyle samo owocow, jablek o `diff`
// wiecej niz pomaranczy. Ile jablek lacznie?

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const BASKETS = [8, 10, 12];
const PER_BASKET = [30, 36, 40, 44, 50];
const DIFFS = [4, 6, 8];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildData({ baskets, perBasket, diff, fixedOptions = null }) {
  const total = baskets * perBasket;
  const applesPer = (perBasket + diff) / 2;
  const answer = applesPer * baskets;

  let options;
  let correctIndex;
  if (fixedOptions) {
    options = fixedOptions.map((value, index) => ({ value, label: LETTERS[index] }));
    correctIndex = fixedOptions.indexOf(answer);
  } else {
    const pool = [answer, (applesPer - 1) * baskets, (applesPer + 1) * baskets, (applesPer + 2) * baskets];
    const unique = [...new Set(pool.filter((v) => v > 0))];
    let extra = 2;
    while (unique.length < 4) {
      const cand = (applesPer + extra) * baskets;
      if (!unique.includes(cand)) unique.push(cand);
      extra += 1;
    }
    const ordered = shuffle(unique.slice(0, 4));
    options = ordered.map((value, index) => ({ value, label: LETTERS[index] }));
    correctIndex = ordered.indexOf(answer);
  }

  return { baskets, perBasket, diff, total, applesPer, answer, options, correctIndex };
}

function createOriginalTask() {
  return {
    type: "E8BasketApplesClosedTask",
    data: buildData({
      baskets: 10,
      perBasket: 40,
      diff: 6,
      fixedOptions: [210, 230, 240, 260],
    }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2026_MAJ",
      taskNumber: 7,
    },
    tags: ["e8", "algebra", "zadanie-tekstowe"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  let baskets;
  let perBasket;
  let diff;
  do {
    baskets = pick(BASKETS);
    perBasket = pick(PER_BASKET);
    diff = pick(DIFFS);
  } while ((perBasket - diff) % 2 !== 0 || perBasket - diff <= 0);

  return {
    type: "E8BasketApplesClosedTask",
    data: buildData({ baskets, perBasket, diff }),
    source: { variant: "random", basedOn: "E8_2026_MAJ_7" },
    tags: ["e8", "algebra", "zadanie-tekstowe"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Do <InlineMath math={`${data.baskets}`} /> pustych koszy włożono jabłka i
        pomarańcze, łącznie <InlineMath math={`${data.total}`} /> sztuk tych owoców.
        W każdym koszu łączna liczba owoców jest taka sama oraz w każdym koszu liczba
        jabłek jest o <InlineMath math={`${data.diff}`} /> większa od liczby
        pomarańczy.
      </p>
      <p>
        Ile sztuk jabłek jest łącznie w tych <InlineMath math={`${data.baskets}`} />{" "}
        koszach? Wybierz właściwą odpowiedź spośród podanych.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={option.value}>
            <ChoiceOptionMath letter={LETTERS[index]} math={`${option.value}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        label: LETTERS[index],
        textNode: LETTERS[index],
      }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={`${data.answer}`} />
    </span>
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[userAnswer]} math={`${option.value}`} />
    </span>
  );
}

export function renderExplanation({ data }) {
  const { baskets, perBasket, diff, total, applesPer, answer } = data;
  const orangesPer = (perBasket - diff) / 2;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Liczba owoców w jednym koszu:
      </p>
      <BlockMath math={`${total}:${baskets}=${perBasket}`} />
      <p>
        <strong>Krok 2.</strong> W koszu jabłek jest o <InlineMath math={`${diff}`} />{" "}
        więcej niż pomarańczy, więc pomarańczy jest{" "}
        <InlineMath math={`(${perBasket}-${diff}):2=${orangesPer}`} />, a jabłek{" "}
        <InlineMath math={`${orangesPer}+${diff}=${applesPer}`} />.
      </p>
      <p>
        <strong>Krok 3.</strong> Łączna liczba jabłek:
      </p>
      <BlockMath math={`${applesPer}\\cdot${baskets}=${answer}`} />
      <p>
        Poprawna odpowiedź to{" "}
        <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.
      </p>
    </div>
  );
}
